/*
  <article-related-products>
  Loads products related to the current article from the LivGood app API and
  renders them as cards.

  Response shapes accepted:
    { "products": [<id>, ...] }  |  [<id>, ...]
  Ids may be numeric or gid://shopify/Product/<id>. If the API instead returns
  objects that already contain product data (handle/title/price), those are
  used directly and no /products.json lookup is performed.
*/
if (typeof customElements.get('article-related-products') === 'undefined') {

  const LG_ARP_CACHE_KEY = 'lg_products_index_v1';
  const LG_ARP_CACHE_TTL = 6 * 60 * 60 * 1000; // 6 hours
  const LG_ARP_MAX_PAGES = 20;

  class ArticleRelatedProducts extends HTMLElement {

    connectedCallback() {
      this.grid = this.querySelector('[data-arp-grid]');
      // Strip any HTML (e.g. currency <span>) so it renders cleanly as text.
      this.moneyFormat = (this.dataset.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '').trim();
      this.limit = parseInt(this.dataset.limit, 10) || 12;
      this.endpoint = this.dataset.endpoint || 'https://livgood-pt.herokuapp.com/api/v2/article/';
      this.articleId = String(this.dataset.articleId || '').replace(/^.*Article\//, '').trim();

      if (!this.grid || !this.articleId) return;
      this.load();
    }

    async load() {
      try {
        const res = await fetch(this.endpoint + encodeURIComponent(this.articleId), {
          headers: { 'Accept': 'application/json' }
        });
        if (!res.ok) return;

        const data = await res.json();
        const items = this.normalize(data);
        if (!items.length) return;

        const products = await this.resolve(items);
        if (!products.length) return;

        this.render(products.slice(0, this.limit));
      } catch (e) {
        console.warn('[article-related-products]', e);
      }
    }

    /* Turn the API payload into a list of { id } or full product objects. */
    normalize(data) {
      let arr = [];
      if (Array.isArray(data)) {
        arr = data;
      } else if (data && Array.isArray(data.products)) {
        arr = data.products;
      }
      return arr
        .map((x) => {
          if (x && typeof x === 'object') return x; // future: full product data
          const id = String(x).replace(/^.*Product\//, '').trim();
          return id ? { id: id } : null;
        })
        .filter(Boolean);
    }

    /* Resolve items to renderable products. */
    async resolve(items) {
      const resolved = [];
      const needIds = [];

      items.forEach((it) => {
        if (it.handle && (it.title || it.price != null)) {
          resolved.push(this.fromApiObject(it));
        } else if (it.id) {
          needIds.push(String(it.id));
        }
      });

      if (needIds.length) {
        const map = await this.getIndex(needIds);
        // preserve the order the API returned
        needIds.forEach((id) => {
          if (map[id]) resolved.push(map[id]);
        });
      }
      return resolved;
    }

    fromApiObject(it) {
      const handle = it.handle || '';
      return {
        id: String(it.id || ''),
        handle: handle,
        title: it.title || '',
        url: it.url || (handle ? '/products/' + handle : '#'),
        image: it.image || it.featured_image || (it.images && it.images[0]) || '',
        price: it.price,
        compare: it.compare_at_price != null ? it.compare_at_price : it.compare,
        available: it.available !== false,
        priceIsCents: false
      };
    }

    /* Build / read a cached id -> product index from /products.json. */
    async getIndex(needIds) {
      let cache = this.readCache();
      const missing = () => needIds.filter((id) => !cache.map[id]);

      if (missing().length === 0) return cache.map;
      if (cache.complete) return cache.map; // nothing more to fetch

      let page = cache.nextPage || 1;
      while (missing().length && page <= LG_ARP_MAX_PAGES) {
        let list;
        try {
          const r = await fetch('/products.json?limit=250&page=' + page);
          if (!r.ok) break;
          const j = await r.json();
          list = j.products || [];
        } catch (e) {
          break;
        }
        if (!list.length) {
          cache.complete = true;
          break;
        }
        list.forEach((p) => {
          cache.map[String(p.id)] = this.slim(p);
        });
        page += 1;
      }

      cache.nextPage = page;
      this.writeCache(cache);
      return cache.map;
    }

    slim(p) {
      const v = (p.variants && p.variants[0]) || {};
      let img = '';
      if (p.images && p.images[0] && p.images[0].src) img = p.images[0].src;
      else if (v.featured_image && v.featured_image.src) img = v.featured_image.src;
      const available = (p.variants || []).some((x) => x.available);
      return {
        id: String(p.id),
        handle: p.handle,
        title: p.title,
        url: '/products/' + p.handle,
        image: img,
        price: v.price, // major units string, e.g. "42.67"
        compare: v.compare_at_price,
        available: available,
        priceIsCents: false
      };
    }

    readCache() {
      try {
        const raw = window.localStorage.getItem(LG_ARP_CACHE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.ts && (Date.now() - parsed.ts) < LG_ARP_CACHE_TTL && parsed.map) {
            return parsed;
          }
        }
      } catch (e) { /* ignore */ }
      return { ts: Date.now(), map: {}, nextPage: 1, complete: false };
    }

    writeCache(cache) {
      try {
        cache.ts = cache.ts || Date.now();
        window.localStorage.setItem(LG_ARP_CACHE_KEY, JSON.stringify(cache));
      } catch (e) { /* storage full / disabled — ignore */ }
    }

    render(products) {
      const frag = document.createDocumentFragment();

      products.forEach((p) => {
        const card = document.createElement('a');
        card.className = 'arp-card element--border-radius';
        if (!p.available) card.classList.add('arp-card--sold-out');
        card.href = p.url;

        const imgWrap = document.createElement('span');
        imgWrap.className = 'arp-card__image';

        const onSale = this.hasCompare(p);
        if (onSale) {
          const badge = document.createElement('span');
          badge.className = 'arp-card__badge';
          badge.textContent = window.KROWN && KROWN.strings && KROWN.strings.sale ? KROWN.strings.sale : 'Sale';
          imgWrap.appendChild(badge);
        }

        if (p.image) {
          const img = document.createElement('img');
          img.src = this.sized(p.image, 400);
          img.alt = p.title || '';
          img.loading = 'lazy';
          img.width = 400;
          img.height = 400;
          imgWrap.appendChild(img);
        }
        card.appendChild(imgWrap);

        const title = document.createElement('span');
        title.className = 'arp-card__title';
        title.textContent = p.title || '';
        card.appendChild(title);

        const price = document.createElement('span');
        price.className = 'arp-card__price';
        price.appendChild(document.createTextNode(this.money(p.price, p.priceIsCents)));
        if (onSale) {
          const del = document.createElement('del');
          del.textContent = this.money(p.compare, p.priceIsCents);
          price.appendChild(del);
        }
        card.appendChild(price);

        frag.appendChild(card);
      });

      this.grid.innerHTML = '';
      this.grid.appendChild(frag);
      this.removeAttribute('hidden');
    }

    hasCompare(p) {
      const price = this.toNumber(p.price, p.priceIsCents);
      const compare = this.toNumber(p.compare, p.priceIsCents);
      return compare > 0 && compare > price;
    }

    toNumber(value, isCents) {
      if (value == null || value === '') return 0;
      let n = parseFloat(String(value).replace(',', '.'));
      if (isNaN(n)) return 0;
      return isCents ? n / 100 : n;
    }

    sized(src, width) {
      if (!src) return src;
      const sep = src.indexOf('?') === -1 ? '?' : '&';
      return src + sep + 'width=' + width;
    }

    /* Format major-unit amount using the shop's money format string. */
    money(value, isCents) {
      const amount = this.toNumber(value, isCents);
      const cents = Math.round(amount * 100);
      return ArticleRelatedProducts.formatMoney(cents, this.moneyFormat);
    }

    static formatMoney(cents, format) {
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || '${{amount}}';

      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = precision == null ? 2 : precision;
        thousands = thousands || ',';
        decimal = decimal || '.';
        if (isNaN(number) || number == null) return '0';
        number = (number / 100).toFixed(precision);
        const parts = number.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const cents = parts[1] ? decimal + parts[1] : '';
        return dollars + cents;
      }

      const match = formatString.match(placeholderRegex);
      let value = '';
      const token = match ? match[1] : 'amount';
      switch (token) {
        case 'amount': value = formatWithDelimiters(cents, 2); break;
        case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
        case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.'); break;
        case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', '.'); break;
        case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
        default: value = formatWithDelimiters(cents, 2);
      }
      return formatString.replace(placeholderRegex, value);
    }
  }

  customElements.define('article-related-products', ArticleRelatedProducts);
}
